import type {
  KatalogTreninguDto,
  TreningDto,
  TreningListaDto,
  WiadomoscWejsciowaDto,
  ZalozenieTreninguDto,
} from "@shared/treningTypy.js";
import { ApiError, request } from "./api.js";

/**
 * Klient tras treningu (/api/trening/*). Kontrakt typow: shared/treningTypy.ts, opis calosci: docs/trening.md.
 *
 * Transport (naglowki, `credentials`, mapowanie bledow na ApiError) niesie `request` z lib/api.ts. Tu jest tylko adres,
 * cialo i sprawdzenie ksztaltu odpowiedzi: kazda odpowiedz o niespodziewanym ksztalcie to blad `zla_odpowiedz` (502), a nie
 * pusty ekran z `undefined` w srodku. Kody bledow serwera zamienia na zdania lib/trening.ts (opisBleduTreningu).
 */

const BAZA = "/api/trening";

function jestObiektem(wartosc: unknown): wartosc is Record<string, unknown> {
  return wartosc !== null && typeof wartosc === "object" && !Array.isArray(wartosc);
}

function zlaOdpowiedz(): ApiError {
  return new ApiError(502, "zla_odpowiedz");
}

/** Katalog: poziomy i cwiczenia (dwie tablice). Brak ktorejkolwiek to blad odpowiedzi, nie "pusty katalog". */
export function katalogZOdpowiedzi(odpowiedz: unknown): KatalogTreninguDto {
  if (!jestObiektem(odpowiedz) || !Array.isArray(odpowiedz.poziomy) || !Array.isArray(odpowiedz.cwiczenia)) {
    throw zlaOdpowiedz();
  }
  return { poziomy: odpowiedz.poziomy, cwiczenia: odpowiedz.cwiczenia } as KatalogTreninguDto;
}

/**
 * Lista treningow konta. Kontrakt (shared/treningTypy.ts, GET /api/trening): GOLA TABLICA. Cokolwiek innego (obiekt z
 * lista w polu, null, tekst) to blad odpowiedzi, a nie pusta lista ani zgadywanie koperty: zmiana ksztaltu po stronie
 * serwera ma wyjsc na jaw w testach i na ekranie, a nie zostac po cichu "naprawiona" tutaj.
 */
export function listaZOdpowiedzi(odpowiedz: unknown): TreningListaDto[] {
  if (Array.isArray(odpowiedz)) return odpowiedz as TreningListaDto[];
  throw zlaOdpowiedz();
}

/**
 * Pojedynczy trening: sam obiekt albo obiekt w polu `trening`. Pola, ktore moga byc null, dostaja null, gdy ich brak
 * (serwer moze je pominac), zeby reszta klienta mogla sprawdzac je jednym `=== null`.
 */
export function treningZOdpowiedzi(odpowiedz: unknown): TreningDto {
  const surowy = jestObiektem(odpowiedz) && jestObiektem(odpowiedz.trening) ? odpowiedz.trening : odpowiedz;
  if (!jestObiektem(surowy) || typeof surowy.id !== "string" || !Array.isArray(surowy.wiadomosci)) {
    throw zlaOdpowiedz();
  }
  return {
    ...(surowy as unknown as TreningDto),
    scenariusz: (surowy.scenariusz as string | null | undefined) ?? null,
    zrodloAnalizyId: (surowy.zrodloAnalizyId as string | null | undefined) ?? null,
    ocena: (surowy.ocena as TreningDto["ocena"] | undefined) ?? null,
    zakonczono: (surowy.zakonczono as string | null | undefined) ?? null,
  };
}

function adresTreningu(id: string): string {
  return `${BAZA}/${encodeURIComponent(id)}`;
}

export const treningApi = {
  /** GET /api/trening/katalog: poziomy trudnosci i cwiczenia z metodyki. */
  katalog: async (): Promise<KatalogTreninguDto> => katalogZOdpowiedzi(await request<unknown>(`${BAZA}/katalog`)),

  /** GET /api/trening: treningi tego konta, od najnowszych. */
  lista: async (): Promise<TreningListaDto[]> => listaZOdpowiedzi(await request<unknown>(BAZA)),

  /** POST /api/trening (201): zalozenie treningu. Odpowiedz ma juz pierwsza wiadomosc rozmowcy. */
  zaloz: async (wejscie: ZalozenieTreninguDto): Promise<TreningDto> =>
    treningZOdpowiedzi(await request<unknown>(BAZA, { method: "POST", body: JSON.stringify(wejscie) })),

  /** GET /api/trening/:id: trening z cala rozmowa (i ocena, gdy zakonczony). */
  pobierz: async (id: string): Promise<TreningDto> => treningZOdpowiedzi(await request<unknown>(adresTreningu(id))),

  /** POST /api/trening/:id/wiadomosc: odpowiedz uzytkownika. Odpowiedz niesie caly trening z odpowiedzia rozmowcy. */
  wyslij: async (id: string, tekst: string): Promise<TreningDto> => {
    const cialo: WiadomoscWejsciowaDto = { tekst };
    return treningZOdpowiedzi(
      await request<unknown>(`${adresTreningu(id)}/wiadomosc`, { method: "POST", body: JSON.stringify(cialo) }),
    );
  },

  /** POST /api/trening/:id/zakoncz: zakonczenie i ocena. Bez ciala, jak wylogowanie. */
  zakoncz: async (id: string): Promise<TreningDto> =>
    treningZOdpowiedzi(await request<unknown>(`${adresTreningu(id)}/zakoncz`, { method: "POST" })),
};
